import { useParams, Link } from "react-router-dom";
import { useEffect, useMemo } from "react";
import ShareToolButton from "@/components/ShareToolButton";
import ToolJsonLd from "@/components/ToolJsonLd";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Globe, Tag, DollarSign, Layers, TrendingUp, GitCompareArrows } from "lucide-react";
import ReviewSection from "@/components/ReviewSection";
import Navbar from "@/components/Navbar";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import FavoriteButton from "@/components/FavoriteButton";
import ToolLogo from "@/components/ToolLogo";
import { AffiliateBadge } from "@/components/AffiliateBadge";
import { useFavorites } from "@/hooks/useFavorites";
import { useAffiliates } from "@/hooks/useAffiliates";
import { useTools } from "@/hooks/useTools";
import { categories } from "@/data/categories";
import { protocols } from "@/data/protocols";
import { useComparisons } from "@/hooks/useComparisons";

const pricingColors: Record<string, string> = {
  Free: "text-primary bg-primary/10 border-primary/20",
  Freemium: "text-blue-400 bg-blue-400/10 border-blue-400/20",
  Paid: "text-amber-400 bg-amber-400/10 border-amber-400/20",
  "Open Source": "text-purple-400 bg-purple-400/10 border-purple-400/20",
};

function slugify(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

const ToolDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { hasAffiliate } = useAffiliates();
  const { tools: allTools, loading: toolsLoading } = useTools();
  const { comparisons } = useComparisons();
  const isAff = slug ? hasAffiliate(slug) : false;

  const tool = useMemo(
    () => allTools.find(t => slugify(t.name) === slug),
    [allTools, slug]
  );

  const category = useMemo(
    () => (tool ? categories.find(c => c.name === tool.category) : undefined),
    [tool]
  );

  const relatedTools = useMemo(() => {
    if (!tool) return [];
    // Same subcategory first, then same category, exclude self
    return allTools
      .filter(t => t.name !== tool.name && t.category === tool.category)
      .sort((a, b) => {
        const aMatch = a.subcategory === tool.subcategory ? 1 : 0;
        const bMatch = b.subcategory === tool.subcategory ? 1 : 0;
        if (bMatch !== aMatch) return bMatch - aMatch;
        if (a.trending && !b.trending) return -1;
        if (!a.trending && b.trending) return 1;
        return 0;
      })
      .slice(0, 8);
  }, [tool, allTools]);

  const relatedProtocols = useMemo(() => {
    if (!tool) return [];
    return protocols.filter(p =>
      p.steps.some(s => s.toolName.toLowerCase() === tool.name.toLowerCase() ||
        s.alternatives?.some(a => a.name.toLowerCase() === tool.name.toLowerCase()))
    ).slice(0, 4);
  }, [tool]);

  const comparisonData = useMemo(() => {
    if (!tool) return undefined;
    for (const comp of comparisons) {
      const found = comp.tools.find(t => t.name.toLowerCase() === tool.name.toLowerCase());
      if (found) return { comparison: comp, toolData: found };
    }
    return undefined;
  }, [tool, comparisons]);

  if (!tool) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <PageBreadcrumb items={[{ label: "Directory", to: "/directory" }, { label: "Tool not found" }]} />
        <div className="container mx-auto px-4 py-20 text-center">
          {toolsLoading ? (
            <p className="text-sm font-mono text-muted-foreground">Loading tool…</p>
          ) : (
            <>
              <h1 className="text-2xl font-mono font-bold text-foreground mb-4">Tool Not Found</h1>
              <Link to="/" className="text-primary font-mono text-sm hover:underline">← Back to directory</Link>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ToolJsonLd
        slug={slug || ""}
        name={tool.name}
        description={tool.description}
        category={tool.category}
        subcategory={tool.subcategory}
        pricing={tool.pricing}
        url={tool.url}
      />
      <Navbar />
      <PageBreadcrumb
        items={[
          { label: "Directory", to: "/directory" },
          { label: tool.name },
        ]}
      />

      <div className="container mx-auto px-4 py-4 sm:py-8 max-w-5xl">
        {/* Breadcrumb */}
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-mono text-muted-foreground hover:text-primary transition-colors mb-4 sm:mb-6">
          <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Back
        </Link>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-10"
        >
          {/* Main info */}
          <div className="lg:col-span-2">
            <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-5">
              <ToolLogo
                name={tool.name}
                logoUrl={tool.logoUrl}
                className="w-12 h-12 sm:w-16 sm:h-16"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <h1 className="text-xl sm:text-3xl font-mono font-extrabold text-foreground break-words">{tool.name}</h1>
                  {tool.trending && (
                    <span className="text-[10px] sm:text-xs px-2 py-0.5 sm:py-1 rounded-full bg-primary/10 text-primary font-mono">🔥</span>
                  )}
                  <FavoriteButton
                    isFavorite={isFavorite(slugify(tool.name))}
                    onClick={() => toggleFavorite(slugify(tool.name))}
                    className="opacity-100"
                  />
                  <ShareToolButton slug={slugify(tool.name)} name={tool.name} />
                </div>
                <p className="text-muted-foreground font-mono text-[11px] sm:text-sm mt-1">{tool.category} · {tool.subcategory}</p>
              </div>
            </div>

            <p className="text-secondary-foreground leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base">{tool.description}</p>

            {/* Compare these — pre-fills /compare with this tool + 2 curated picks.
                Scoring (highest wins) prefers tools that:
                  • share a curated comparison with the current tool (apples-to-apples) — +6
                  • have a high curated rating (0–5 mapped to 0–5) — adds rating
                  • exist in any curated comparison (richer matrix) — +3
                  • match the same subcategory — +2
                  • are trending — +1
                We score the full same-category pool (not the pre-truncated
                related list) so the chip surfaces the strongest picks even
                when 8+ siblings exist. */}
            {(() => {
              // Map slug → curated rating (0–5) across all comparisons.
              const ratingBySlug = new Map<string, number>();
              const curatedSlugs = new Set<string>();
              comparisons.forEach(c =>
                c.tools.forEach(t => {
                  const s = slugify(t.name);
                  curatedSlugs.add(s);
                  // Keep the highest rating if a tool appears in multiple comparisons.
                  const prev = ratingBySlug.get(s) ?? 0;
                  if (typeof t.rating === "number" && t.rating > prev) {
                    ratingBySlug.set(s, t.rating);
                  }
                })
              );
              // Slugs that appear in the SAME curated comparison as this tool.
              const currentSlug = slugify(tool.name);
              const coComparedSlugs = new Set<string>();
              comparisons.forEach(c => {
                if (c.tools.some(t => slugify(t.name) === currentSlug)) {
                  c.tools.forEach(t => coComparedSlugs.add(slugify(t.name)));
                }
              });
              coComparedSlugs.delete(currentSlug);

              const pool = allTools.filter(
                t => t.name !== tool.name && t.category === tool.category,
              );
              const score = (t: typeof tool) => {
                const s = slugify(t.name);
                let n = 0;
                if (coComparedSlugs.has(s)) n += 6;
                if (curatedSlugs.has(s)) n += 3;
                n += ratingBySlug.get(s) ?? 0; // 0–5
                if (t.subcategory === tool.subcategory) n += 2;
                if (t.trending) n += 1;
                return n;
              };
              const picks = pool
                .map(t => ({ t, s: score(t) }))
                .sort((a, b) => {
                  if (b.s !== a.s) return b.s - a.s;
                  // Tie-breaker: alphabetical for deterministic output.
                  return a.t.name.localeCompare(b.t.name);
                })
                .slice(0, 2)
                .map(x => x.t);
              if (picks.length < 1) return null;
              const ids = [currentSlug, ...picks.map(p => slugify(p.name))].join(",");
              return (
                <div className="mb-6 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-secondary/30 px-3 py-2">
                  <GitCompareArrows className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span className="text-xs font-mono text-muted-foreground shrink-0">Compare with:</span>
                  <span className="text-xs font-mono text-foreground truncate">
                    {picks.map(p => p.name).join(" · ")}
                  </span>
                  <Link
                    to={`/compare?ids=${ids}`}
                    className="ml-auto text-[11px] font-mono px-3 py-1 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    Compare these →
                  </Link>
                </div>
              );
            })()}

            {/* Live screenshot of tool homepage (Microlink free tier) */}
            {tool.url && (
              <a
                href={`/go/${slug}`}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="block rounded-xl border border-border bg-secondary/30 overflow-hidden mb-6 group relative"
                title={`Open ${tool.name}`}
              >
                <div className="aspect-video relative grid-bg">
                  <img
                    src={`https://api.microlink.io/?url=${encodeURIComponent(tool.url)}&screenshot=true&meta=false&embed=screenshot.url&viewport.width=1280&viewport.height=720&waitFor=1500`}
                    alt={`${tool.name} homepage screenshot`}
                    loading="lazy"
                    className="w-full h-full object-cover object-top transition-transform group-hover:scale-[1.01]"
                    onError={(e) => {
                      const img = e.currentTarget;
                      img.style.display = "none";
                      const fb = img.nextElementSibling as HTMLElement | null;
                      if (fb) fb.style.display = "flex";
                    }}
                  />
                  <div
                    className="absolute inset-0 hidden items-center justify-center"
                    style={{ display: "none" }}
                  >
                    <div className="text-center">
                      <Globe className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                      <p className="text-sm font-mono text-muted-foreground/50">
                        Preview unavailable for {tool.name}
                      </p>
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-background/85 backdrop-blur border border-border text-[10px] font-mono text-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                    Open {tool.name} <ExternalLink className="w-3 h-3" />
                    {isAff && <AffiliateBadge className="ml-1" />}
                  </div>
                </div>
              </a>
            )}

            {/* Comparison data if available */}
            {comparisonData && (
              <div className="rounded-xl border border-border bg-card p-4 sm:p-5 mb-6">
                <h3 className="font-mono font-bold text-foreground text-sm mb-3 sm:mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" /> Detailed Analysis
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                  <div>
                    <h5 className="text-[10px] font-mono text-primary mb-2">PROS</h5>
                    {comparisonData.toolData.pros.map((pro, i) => (
                      <div key={i} className="text-xs text-secondary-foreground flex items-start gap-1.5 mb-1.5">
                        <span className="text-primary shrink-0">✓</span> {pro}
                      </div>
                    ))}
                  </div>
                  <div>
                    <h5 className="text-[10px] font-mono text-red-400 mb-2">CONS</h5>
                    {comparisonData.toolData.cons.map((con, i) => (
                      <div key={i} className="text-xs text-secondary-foreground flex items-start gap-1.5 mb-1.5">
                        <span className="text-red-400 shrink-0">✗</span> {con}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border-t border-border pt-3">
                  <h5 className="text-[10px] font-mono text-muted-foreground mb-2">FEATURES</h5>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(comparisonData.toolData.features).map(([key, val]) => (
                      <div key={key} className="flex items-center gap-1.5 text-[11px] font-mono">
                        {val === true ? (
                          <span className="text-primary">✓</span>
                        ) : val === false ? (
                          <span className="text-muted-foreground/40">✗</span>
                        ) : (
                          <span className="text-amber-400">~</span>
                        )}
                        <span className="text-secondary-foreground">{key}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Quick Info Card */}
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-mono font-bold text-foreground text-sm mb-4">Quick Info</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-[10px] font-mono text-muted-foreground">Pricing</p>
                    <span className={`text-xs font-mono px-2 py-0.5 rounded-full border ${pricingColors[tool.pricing]}`}>
                      {tool.pricing}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Layers className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-[10px] font-mono text-muted-foreground">Category</p>
                    <p className="text-xs font-mono text-foreground">{category?.icon} {tool.category}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Tag className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-[10px] font-mono text-muted-foreground">Subcategory</p>
                    <p className="text-xs font-mono text-foreground">{tool.subcategory}</p>
                  </div>
                </div>
                {comparisonData && (
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-[10px] font-mono text-muted-foreground">Rating</p>
                      <p className="text-xs font-mono text-amber-400">★ {comparisonData.toolData.rating}/5 — {comparisonData.toolData.bestFor}</p>
                    </div>
                  </div>
                )}
              </div>
              <a
                href={`/go/${slug}`}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="mt-5 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-primary-foreground font-mono text-sm font-semibold hover:bg-primary/90 transition-colors"
              >
                Visit {tool.name} <ExternalLink className="w-3.5 h-3.5" />
                {isAff && <AffiliateBadge className="ml-1 bg-primary-foreground/10 border-primary-foreground/30 text-primary-foreground" />}
              </a>
            </div>

            {/* Used in Protocols */}
            {relatedProtocols.length > 0 && (
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-mono font-bold text-foreground text-sm mb-3">📋 Used in Protocols</h3>
                <div className="space-y-2">
                  {relatedProtocols.map(p => (
                    <Link
                      key={p.id}
                      to={`/?tab=protocols&q=${encodeURIComponent(tool.name)}`}
                      className="block p-3 rounded-lg bg-secondary/50 border border-border hover:border-primary/30 transition-colors"
                    >
                      <p className="text-xs font-mono font-semibold text-foreground line-clamp-1">{p.title}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{p.steps.length} steps · {p.difficulty}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Reviews Section */}
        <div className="mb-10">
          <ReviewSection toolSlug={slug || ""} />
        </div>

        {/* Related Tools */}
        {relatedTools.length > 0 && (
          <div className="mb-10">
            <h2 className="text-lg font-mono font-bold text-foreground mb-4">
              <span className="text-primary">&gt;</span> Similar Tools in {tool.category}
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
              {relatedTools.map(rt => (
                <Link
                  key={`${rt.name}-${rt.category}`}
                  to={`/tool/${slugify(rt.name)}`}
                  className="group flex flex-col p-4 rounded-xl bg-card border border-border card-hover"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <ToolLogo name={rt.name} logoUrl={rt.logoUrl} />
                    <div className="min-w-0">
                      <h3 className="font-mono font-semibold text-sm text-foreground truncate group-hover:text-primary transition-colors">
                        {rt.name}
                        {rt.trending && <span className="ml-1 text-[10px]">🔥</span>}
                      </h3>
                      <p className="text-[10px] text-muted-foreground font-mono">{rt.subcategory}</p>
                    </div>
                  </div>
                  <p className="text-[11px] text-secondary-foreground line-clamp-2 flex-1">{rt.description}</p>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border mt-2 self-start ${pricingColors[rt.pricing]}`}>
                    {rt.pricing}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm font-mono text-muted-foreground">
            © 2026 <span className="text-foreground">HackerLabs</span><span className="text-primary">.ai</span> — The largest AI Directory
          </p>
        </div>
      </footer>
    </div>
  );
};

export { slugify };
export default ToolDetail;
